import React, { useState, useEffect, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useParams, useHistory, useLocation } from "react-router-dom";
import {
  Box,
  Typography,
  Divider,
  Stack,
  Button,
  TextField,
  Checkbox,
  FormControlLabel,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Collapse,
  useMediaQuery,
  useTheme,
  FormControl,
  Menu,
  MenuItem
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import { DatePicker, TimePicker } from "@mui/x-date-pickers";
import dayjs from "dayjs";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import { messageShow, uploadSingle } from "../../common/api/actions";
import { getImageSource } from "../api/routes";
import { createEvent, updateEmail } from "../api/action/mutation";
import ToSelector from "../components/ToSelector";
import RichTextEditor from "../../common/HtmlEditor";
import { sanitizeEmailHtml } from "../../../setup/helpers";

const defaultPermissions = {
  canCreate: false,
  canEdit: false,
  canDelete: false,
  canView: false,
};

const BackButton = ({ onClick }) => (
  <IconButton color="primary" onClick={onClick} aria-label="Go back">
    <ArrowBackIcon />
  </IconButton>
);

const CreateUpdate = ({ refreshList, updateExistingEvent, permissions = defaultPermissions }) => {
  const dispatch = useDispatch();
  const history = useHistory();
  const settings = useSelector(state => state.settings);
  const isMarketingSubscribed = settings?.isMessagingSubscribed || settings?.isCampaignSubscribed;
  const { id, type } = useParams();
  const location = useLocation();
  const ckEditorRef = useRef(null);
  const isEditMode = !!id;
  const rewardData = location.state?.rewardData;
  const theme = useTheme();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down("md"));
  const isMediumScreen = useMediaQuery(theme.breakpoints.between("sm", "md"));
  const [showFilters, setShowFilters] = useState(!isSmallScreen);
  const [filterModalOpen, setFilterModalOpen] = useState(false);
  const [scheduleOnly, setScheduleOnly] = useState(() => {
    const scheduledTime = location.state?.eventData?.scheduledTime;
    return scheduledTime && dayjs(scheduledTime).isValid();
  });
  const [recipientDisplay, setRecipientDisplay] = useState("All");
  const [anchorEl, setAnchorEl] = useState(null);

  const placeholderLabels = {
    "vendor.name": "Vendor Name",
    "vendor.email": "Vendor Email",
    "vendor.address": "Vendor Address",
    "vendor.mobile": "Vendor Mobile",
    "user.name": "User Name",
    "user.email": "User Email",
    "user.mobile": "User Mobile",
  };

  const [eventData, setEventData] = useState(() => {
    let data = location.state?.eventData || {
      name: "",
      date: dayjs(),
      message: "",
      subject: "",
      scheduledTime: null,
      for: "all",
    };
    if (rewardData) {
      if (rewardData.type === "goal") {
        const goalName = rewardData.goalName.charAt(0).toUpperCase() + rewardData.goalName.slice(1);
        data = {
          name: `${goalName} (Goal)`,
          date: dayjs(),
          message: `🚀 Ready to crush your goals? Complete "${goalName}" and unlock ${rewardData.rewardPoints} points! 🎁 Don't wait—this offer disappears on ${dayjs(rewardData.expiryDate).format("D MMMM, YYYY")}! Let's make it happen!`,
          subject: `🍕 Earn ${rewardData.rewardPoints} points by ${goalName}`,
          scheduledTime: null,
          for: "all",
        };
      } else if (rewardData.type === "leaderboard") {
        data = {
          name: `${rewardData.user.name} (Leaderboard)`,
          date: dayjs(),
          message: `🎉 Bravo, ${rewardData.user.name}! Your taste for rewards is exquisite!\n${
            rewardData.totalEarnedPoints > 0 ? `\n🍽️ You've savored ${rewardData.totalEarnedPoints} mouth-watering points\n` : ""
          }${
            rewardData.totalRedeemedPoints > 0 ? `\n🥂 Indulged in ${rewardData.totalRedeemedPoints} points of culinary delights\n` : ""
          }${
            rewardData.totalRewardsUsed > 0 ? `\n🍰 Enjoyed ${rewardData.totalRewardsUsed} delectable rewards\n` : ""
          }${
            rewardData.walletBalance > 0 ? `\n\nYour current menu of points: ${rewardData.walletBalance} scrumptious morsels! 😋\n` : ""
          }\nKeep dining with us and climbing our taste-tastic leaderboard!\n\nHappy dining!`,
          subject: `🍳 ${rewardData.user.name}, you're cooking up a storm! Check your tasty stats!`,
          for: "custom",
          customEmails: [rewardData?.user?.email.toLowerCase()],
          scheduledTime: null,
        };
      } else if (rewardData.type === "reward") {
        data = {
          name: `${rewardData.rewardName} (Reward)`,
          date: dayjs(),
          message: `🎉 You've unlocked a special reward: ${rewardData.rewardName}!\n\n• ${rewardData.earnedPoints} points + ${rewardData.earnedValues} bonus points\n• Offline Deals: ${
            rewardData.offlineDeals ? "✔️" : "❌"
          }\n• Online: ${rewardData.onlineDeals.discountPercentage}% off (Min. ₹${rewardData.onlineDeals.minOrderAmount})\n• Code: ${
            rewardData.voucherCode
          }\n• Limited to first ${rewardData.issueVolume} redemptions\n• Valid: ${rewardData.startDate} - ${rewardData.expiryDate}\n\nUse your reward before it expires and enjoy the benefits!`,
          subject: `🍳 ${rewardData.rewardName} Rewards: Your Taste for Rewards is Exquisite! 🍽️`,
          scheduledTime: null,
          for: "all",
        };
      }
    }
    if (data.scheduledTime && dayjs(data.scheduledTime).isValid()) {
      const scheduled = dayjs(data.scheduledTime);
      data.date = scheduled.startOf("day");
    } else if (data.date) {
      const parsedDate = dayjs(data.date);
      data.date = parsedDate.isValid() ? parsedDate.startOf("day") : dayjs().startOf("day");
    }
    return data;
  });

  const [eventTime, setEventTime] = useState(() => {
    if (scheduleOnly && eventData.scheduledTime && dayjs(eventData.scheduledTime).isValid()) {
      return dayjs(eventData.scheduledTime);
    }
    return dayjs();
  });

  const [notificationType, setNotificationType] = useState("email");
  const [forValue, setForValue] = useState(eventData.for || "all");
  const [customerJoinDays, setCustomerJoinDays] = useState(location.state?.eventData?.customerJoinDays || "");
  const [isFormValid, setIsFormValid] = useState(false);
  const [emails, setEmails] = useState(eventData.customEmails?.join(",").toLowerCase() || "");
  const [localForValue, setLocalForValue] = useState(forValue);
  const [localEmails, setLocalEmails] = useState(emails);
  const [localCustomerJoinDays, setLocalCustomerJoinDays] = useState(customerJoinDays);

  useEffect(() => {
    setShowFilters(!isSmallScreen);
  }, [isSmallScreen]);

  useEffect(() => {
    checkFormValidity();
  }, [eventData, notificationType, forValue, emails, customerJoinDays]);

  useEffect(() => {
    if (forValue === "custom") {
      const emailCount = emails.split(',').filter(e => e.trim()).length;
      setRecipientDisplay(emailCount > 0 ? `Custom (${emailCount})` : "Custom Emails");
    } else if (forValue === "customers") {
      setRecipientDisplay("Customers");
    } else if (forValue === "Customer") {
      setRecipientDisplay("Customers (CRM)");
    } else if (forValue === "Contact") {
      setRecipientDisplay("Contacts");
    } else if (forValue === "Lead") {
      setRecipientDisplay("Leads");
    } else if (forValue === "Prospect") {
      setRecipientDisplay("Prospects");
    } else if (eventData.context === "NEWSLETTER") {
      setRecipientDisplay("Newsletter Subscribers");
    } else {
      setRecipientDisplay("All");
    }
  }, [forValue, emails, eventData.context]);

  useEffect(() => {
    if (filterModalOpen) {
      setLocalForValue(forValue);
      setLocalEmails(emails);
      setLocalCustomerJoinDays(customerJoinDays);
    }
  }, [filterModalOpen, forValue, emails, customerJoinDays]);

  const checkFormValidity = () => {
    const hasText = !!eventData.message?.replace(/<[^>]+>/g, "").trim();
    const isMessageValid = hasText;
    const isCustomValid = eventData.for !== "custom" || (eventData.for === "custom" && eventData.customEmails.length > 0);
    let isValid = eventData.name && eventData.date && isMessageValid && eventData.subject && isCustomValid;
    setIsFormValid(isValid);
  };

  const isValidEmail = (email) => {
    const regex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return regex.test(email);
  };

  const handleMenuClick = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handlePlaceholderSelect = (placeholder) => {
    handlePlaceholderClick(placeholder);
  };

  const handlePlaceholderClick = (placeholder) => {
    const editor = ckEditorRef.current;
    if (editor) {
      let viewFragment;
      if (placeholder === "vendor.address") {
        const addressPlaceholders = "{{vendor.street1}}, {{vendor.street2}}, {{vendor.city}}, {{vendor.country}}";
        viewFragment = editor.data.processor.toView(addressPlaceholders);
      } else {
        viewFragment = editor.data.processor.toView(`{{${placeholder}}}`);
      }
      const modelFragment = editor.data.toModel(viewFragment);
      editor.model.change((writer) => {
        const selection = editor.model.document.selection;
        editor.model.insertContent(modelFragment, selection);
      });
    }
  };

  const handleModalClose = () => {
    setForValue(localForValue);
    setEmails(localEmails);
    setCustomerJoinDays(localCustomerJoinDays);
    setEventData(prev => ({
      ...prev,
      for: localForValue,
      customEmails: localForValue === 'custom' ? localEmails.split(',').map(e => e.trim()) : [],
      customerJoinDays: localCustomerJoinDays
    }));
    setFilterModalOpen(false);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!eventData.date || !eventTime) {
      dispatch(messageShow("Please select a valid date and time"));
      return;
    }
    const combinedDateTime = eventData.date
      .hour(eventTime.hour())
      .minute(eventTime.minute())
      .second(eventTime.second())
      .millisecond(0);
    if (scheduleOnly && combinedDateTime.isBefore(dayjs())) {
      dispatch(messageShow("Scheduled time cannot be in the past"));
      return;
    }

    let computedContext = eventData.context;
    if ((isSmallScreen || isMediumScreen) && forValue === "all" && !computedContext) {
      computedContext = "store";
    }

    let payloadFor = forValue;
    let payloadSelectedSubfilters = eventData.selectedSubfilters || "";
    let payloadGrowthStage = eventData.growthStage || "";
    let payloadBusinessSize = eventData.businessSize || "";
    let payloadCustomEmails = [];

    if (computedContext === "store") {
      if (payloadFor === "all") {
        payloadSelectedSubfilters = "";
        payloadGrowthStage = "";
        payloadBusinessSize = "";
        payloadCustomEmails = [];
      } else if (payloadFor === "customers") {
        payloadGrowthStage = "";
        payloadBusinessSize = "";
        payloadCustomEmails = [];
      } else if (payloadFor === "custom") {
        const emailArray = emails.split(",").map(email => email.trim().toLowerCase()).filter(email => email);
        if (emailArray.length === 0) {
          dispatch(messageShow("Please provide at least one valid custom email."));
          return;
        }
        payloadCustomEmails = emailArray;
        payloadSelectedSubfilters = "";
        payloadGrowthStage = "";
        payloadBusinessSize = "";
      } else {
        payloadSelectedSubfilters = "";
        payloadGrowthStage = "";
        payloadBusinessSize = "";
        payloadCustomEmails = [];
      }
    } else if (computedContext === "CRM") {
      payloadSelectedSubfilters = eventData.selectedSubfilters || "";
      payloadGrowthStage = eventData.growthStage || "";
      payloadBusinessSize = eventData.businessSize || "";
      payloadCustomEmails = eventData.customEmails || [];
    } else {
      payloadSelectedSubfilters = eventData.selectedSubfilters || "";
      payloadGrowthStage = eventData.growthStage || "";
      payloadBusinessSize = eventData.businessSize || "";
      payloadCustomEmails = eventData.customEmails || [];
    }

    let eventPayload = {
      name: eventData.name,
      message: eventData.message,
      notificationType: notificationType,
      for: payloadFor,
      selectedSubfilters: payloadSelectedSubfilters,
      growthStage: payloadGrowthStage,
      businessSize: payloadBusinessSize,
      customerJoinDays: customerJoinDays,
      context: computedContext,
      date: combinedDateTime.toISOString(),
      scheduledTime: scheduleOnly ? combinedDateTime.toISOString() : null,
      customEmails: payloadCustomEmails,
    };
    if (notificationType !== "fb-post" && payloadFor === "custom" && payloadCustomEmails.length > 0) {
      eventPayload.emails = payloadCustomEmails;
    }
    if (notificationType === "email") {
      eventPayload.subject = eventData.subject;
    }
    const tempDiv = document.createElement("div");
    tempDiv.innerHTML = eventData.message;
    const imgTags = Array.from(tempDiv.querySelectorAll("img"));
    for (let img of imgTags) {
      const src = img.getAttribute("src");
      if (src?.startsWith("blob:") || src?.startsWith("data:")) {
        try {
          const blob = await fetch(src).then((res) => res.blob());
          const fileName = `image_${Date.now()}.jpg`;
          const formData = new FormData();
          formData.append("type", "event");
          formData.append("name", fileName);
          formData.append("file", blob);
          const uploadRes = await uploadSingle(formData);
          if (uploadRes?.data?.success) {
            const relativePath = uploadRes.data.file.replace(/\\/g, "/");
            const uploadedUrl = getImageSource(relativePath);
            img.setAttribute("src", uploadedUrl);
          } else {
            dispatch(messageShow("Failed to upload image."));
            return;
          }
        } catch (err) {
          console.error("Image upload error:", err);
          dispatch(messageShow("Image upload failed."));
          return;
        }
      }
    }
    eventPayload.message = sanitizeEmailHtml(tempDiv.innerHTML);
    if (rewardData?.type === "leaderboard") {
      eventPayload.variant = "leaderboard";
    }
    try {
      let response;
      if (isEditMode) {
        switch (type) {
          case "email":
            response = await updateEmail(id, eventPayload);
            break;
          default:
            throw new Error("Invalid event type");
        }
      } else {
        response = await createEvent(eventPayload);
      }
      if (response && response.error) {
        throw new Error(response.error);
      } else if (response && response.message) {
        if(response?.status===200){
          dispatch(messageShow(response.message))
        }else{
          dispatch(messageShow(`Event ${isEditMode ? "Updated" : "Created"} successfully`));
        }
        if (isEditMode) {
          updateExistingEvent(response.data);
        }
        if (refreshList) refreshList();
        setEventData({
          name: "",
          date: dayjs(),
          message: "",
          subject: "",
          for: "all",
        });
        setEventTime(dayjs());
        setEmails("");
        setForValue("all");
        setCustomerJoinDays("");
        setScheduleOnly(false);
      }
    } catch (err) {
      console.error("Error submitting form:", err);
      dispatch(messageShow(err.message || "An error occurred while processing your request"));
    } finally {
      history.push("/messaging");
    }
  };

  const handleCancel = () => {
    history.goBack();
  };

  const handleInputChange = (event) => {
    const { name, value } = event.target;
    setEventData({ ...eventData, [name]: value });
  };

  const handleDateChange = (date) => {
    if (date && date.isValid()) {
      setEventData((prevData) => ({ ...prevData, date }));
      if (!eventTime) setEventTime(date);
    } else {
      setEventData((prevData) => ({ ...prevData, date: null }));
      dispatch(messageShow("Invalid date selected. Please choose a valid date."));
    }
  };

  const getEventTypeHeading = () => {
    switch (type) {
      case "email":
        return "Email";
      default:
        return "Event";
    }
  };

  const openFilterModal = () => setFilterModalOpen(true);

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        width: "100%",
        marginTop: "10px",
        padding: "18px",
        alignItems: "center",
      }}
    >
      {isEditMode && (
        <>
          <Box sx={{ display: "flex", alignItems: "center", width: "90%" }}>
            <BackButton onClick={handleCancel} />
            <Typography variant="subtitle2" sx={{ ml: 1 }}>
              {`${getEventTypeHeading()}: ${eventData.name}`}
            </Typography>
          </Box>
          <Divider sx={{ mb: 1, width: "90%" }} />
        </>
      )}
      {!isSmallScreen && (
        <Stack direction={isMediumScreen ? "column" : "row"} spacing={1} alignItems="center" flexWrap="wrap" sx={{ mb: 2, width: "90%" }}>
          {!isEditMode && <BackButton onClick={handleCancel} />}
          <TextField
            label="Event Name"
            name="name"
            variant="standard"
            value={eventData.name}
            onChange={handleInputChange}
            required
            sx={{ minWidth: 100, flex: "1 1 150px" }}
            disabled={!permissions.canEdit && isEditMode}
          />
          <TextField
            label="Subject"
            name="subject"
            variant="standard"
            value={eventData.subject}
            onChange={handleInputChange}
            required
            sx={{ minWidth: 125, flex: "1 1 200px" }}
            disabled={!permissions.canEdit && isEditMode}
          />
          <FormControl size="small" sx={{ minWidth: 180, flex: "0 0 auto" }}>
            <Button
              variant="outlined"
              onClick={handleMenuClick}
            >
              Insert Placeholder
            </Button>
            <Menu
              anchorEl={anchorEl}
              open={Boolean(anchorEl)}
              onClose={handleMenuClose}
            >
              {Object.keys(placeholderLabels).map((key) => (
                <MenuItem key={key} value={key} onClick={() => handlePlaceholderSelect(key)}>
                  {placeholderLabels[key]}
                </MenuItem>
              ))}
            </Menu>
          </FormControl>
          <Button
            variant="outlined"
            onClick={openFilterModal}
            sx={{
              minWidth: 100,
              justifyContent: 'space-between'
            }}
          >
            To: {recipientDisplay}
            {filterModalOpen ? <ExpandLessIcon /> : <ExpandMoreIcon />}
          </Button>
          <FormControlLabel
            control={
              <Checkbox
                checked={scheduleOnly}
                onChange={(e) => setScheduleOnly(e.target.checked)}
                color="primary"
                disabled={!permissions.canEdit && isEditMode}
              />
            }
            label="Schedule"
          />
          <DatePicker
            value={eventData.date}
            onChange={handleDateChange}
            format="DD/MM/YYYY"
            required
            disabled={!permissions.canEdit && isEditMode}
            minDate={dayjs()}
             sx={{
              minWidth: 120,
              maxWidth: 160,
              flex: "1 1 120px",
              marginTop: "10px",
              '& .MuiOutlinedInput-notchedOutline': {
                border: 'none',
              },
            }}
          />
          <TimePicker
            value={eventTime}
            onChange={(newTime) => setEventTime(newTime)}
            disabled={(!permissions.canEdit && isEditMode) || !scheduleOnly}
            minTime={dayjs()}
            ampm={false}
             sx={{
              minWidth: 100,
              maxWidth: 150,
              flex: "1 1 120px",
              marginTop: "10px",
              '& .MuiOutlinedInput-notchedOutline': {
                border: 'none',
              },
            }}
          />
        </Stack>
      )}
      {isSmallScreen && (
        <>
          <Button
            variant="text"
            endIcon={showFilters ? <ExpandLessIcon /> : <ExpandMoreIcon />}
            onClick={() => setShowFilters((v) => !v)}
            sx={{
              width: "auto",
              padding: "6px 12px",
              fontSize: "0.875rem",
            }}
          >
            {showFilters ? "Hide Options" : "Show Options"}
          </Button>
          <Collapse in={showFilters} timeout="auto" unmountOnExit sx={{ width: "90%" }}>
            <Stack spacing={2} sx={{ mb: 2 }}>
              <TextField
                label="Event Name"
                name="name"
                variant="standard"
                value={eventData.name}
                onChange={handleInputChange}
                required
                disabled={!permissions.canEdit && isEditMode}
                fullWidth
              />
              <TextField
                label="Subject"
                name="subject"
                variant="standard"
                value={eventData.subject}
                onChange={handleInputChange}
                required
                disabled={!permissions.canEdit && isEditMode}
                fullWidth
              />
              <FormControl size="small" sx={{ minWidth: 150, flex: "0 0 auto" }}>
                <Button variant="outlined" onClick={handleMenuClick} >
                  Insert Placeholder
                </Button>
                <Menu
                  anchorEl={anchorEl}
                  open={Boolean(anchorEl)}
                  onClose={handleMenuClose}
                >
                  {Object.keys(placeholderLabels).map((key) => (
                    <MenuItem key={key} value={key} onClick={() => handlePlaceholderSelect(key)}>
                      {placeholderLabels[key]}
                    </MenuItem>
                  ))}
                </Menu>
              </FormControl>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={scheduleOnly}
                    onChange={(e) => setScheduleOnly(e.target.checked)}
                    color="primary"
                    disabled={!permissions.canEdit && isEditMode}
                  />
                }
                label="Schedule"
              />
              <DatePicker
                label="Event Date*"
                value={eventData.date}
                onChange={handleDateChange}
                format="DD/MM/YYYY"
                required
                disabled={!permissions.canEdit && isEditMode}
                minDate={dayjs()}
                slotProps={{ textField: { fullWidth: true } }}
              />
              <TimePicker
                label="Event Time"
                value={eventTime}
                onChange={(newTime) => setEventTime(newTime)}
                disabled={(!permissions.canEdit && isEditMode) || !scheduleOnly}
                minTime={dayjs()}
                ampm={false}
                slotProps={{ textField: { fullWidth: true } }}
              />
              <Button
                variant="outlined"
                onClick={openFilterModal}
                sx={{
                  justifyContent: 'space-between'
                }}
              >
                Recipients: {recipientDisplay}
                {filterModalOpen ? <ExpandLessIcon /> : <ExpandMoreIcon />}
              </Button>
            </Stack>
          </Collapse>
        </>
      )}

      <Box
        sx={{
          width: "90%",
          minHeight: 400,
          ".ck-editor__editable_inline": {
            minHeight: 400,
            padding: "10px",
          },
        }}
      >
        <RichTextEditor
          value={eventData.message}
          onChange={(data) => setEventData((prev) => ({ ...prev, message: data }))}
          editorRef={ckEditorRef}
          setEventData={setEventData}
        />
      </Box>

      <Dialog open={filterModalOpen} onClose={handleModalClose} maxWidth="sm" fullWidth>
        <DialogTitle>Filter Details</DialogTitle>
        <DialogContent dividers>
          <ToSelector
            forValue={localForValue}
            setForValue={setLocalForValue}
            emails={localEmails}
            setEmails={setLocalEmails}
            customerJoinDays={localCustomerJoinDays}
            setCustomerJoinDays={setLocalCustomerJoinDays}
            permissions={permissions}
            isEditMode={isEditMode}
            eventData={eventData}
            setEventData={setEventData}
            selectedSubfilters={eventData.selectedSubfilters || ""}
            setSelectedSubfilters={(value) =>
              setEventData(prev => ({ ...prev, selectedSubfilters: value }))
            }
            isMarketingSubscribed={isMarketingSubscribed}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleModalClose}>Save</Button>
        </DialogActions>
      </Dialog>

      <Box display="flex" justifyContent="flex-start" mt={4} gap={2} sx={{ width: "90%" }}>
        <Button
          variant="contained"
          color="primary"
          type="submit"
          disabled={!isFormValid}
          onClick={handleSubmit}
        >
          {isEditMode ? "Update" : "Create Event"}
        </Button>
        <Button variant="outlined" onClick={handleCancel}>
          Cancel
        </Button>
      </Box>
    </Box>
  );
};

export default CreateUpdate;
